import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '@/api/axios';
import { Plus, Trash, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const Templates = () => {
  const { selectedBusiness } = useOutletContext();
  const [templates, setTemplates] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ name: '', content: '', category: '' });

  useEffect(() => {
    if (selectedBusiness) fetchTemplates();
  }, [selectedBusiness]);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates', { params: { business_id: selectedBusiness.id } });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await api.put(`/templates/${editingTemplate.id}`, formData);
        toast.success('Plantilla actualizada');
      } else {
        await api.post('/templates', { ...formData, business_id: selectedBusiness.id });
        toast.success('Plantilla creada');
      }
      setIsDialogOpen(false);
      setEditingTemplate(null);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error('Error al guardar plantilla');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({ name: template.name, content: template.content, category: template.category || '' });
    setIsDialogOpen(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Eliminar esta plantilla?')) return;
    try {
      await api.delete(`/templates/${templateId}`);
      toast.success('Plantilla eliminada');
      fetchTemplates();
    } catch (error) {
      toast.error('Error al eliminar plantilla');
    }
  };

  const resetForm = () => setFormData({ name: '', content: '', category: '' });

  if (!selectedBusiness) return <div className="h-full flex items-center justify-center"><p className="text-sm text-slate-400">Selecciona un negocio</p></div>;

  return (
    <div className="h-full p-6 overflow-y-auto" data-testid="templates-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-4xl tracking-tight font-black text-white mb-2">Plantillas</h1>
          <p className="text-sm text-slate-400">{selectedBusiness.name} - {templates.length} plantillas</p>
        </div>
        <Button onClick={() => { setEditingTemplate(null); resetForm(); setIsDialogOpen(true); }} className="bg-[#1D4ED8] hover:bg-[#1E40AF]" data-testid="create-template-btn">
          <Plus size={18} weight="bold" className="mr-2" /> Nueva Plantilla
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.length === 0 ? (
          <div className="col-span-full bg-[#111827] border border-white/10 p-8 rounded-[12px] text-center">
            <p className="text-sm text-slate-400">No hay plantillas creadas</p>
          </div>
        ) : templates.map((template) => (
          <div key={template.id} className="bg-[#111827] border border-white/10 p-4 rounded-[12px] hover:border-[#1D4ED8] transition-colors" data-testid={`template-card-${template.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-base font-bold text-white mb-1">{template.name}</h3>
                {template.category && <span className="text-xs px-2 py-1 bg-[#0F172A] border border-white/10 rounded-[12px]">{template.category}</span>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => handleEdit(template)} className="p-2 hover:bg-[#0F172A] text-[#1D4ED8] transition-colors rounded-[12px]" data-testid={`edit-template-${template.id}`}>
                  <PencilSimple size={16} weight="bold" />
                </button>
                <button onClick={() => handleDelete(template.id)} className="p-2 hover:bg-[#DC2626]/10 text-[#DC2626] transition-colors rounded-[12px]" data-testid={`delete-template-${template.id}`}>
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-slate-400 whitespace-pre-wrap">{template.content}</p>
          </div>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) { setEditingTemplate(null); resetForm(); } }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Editar Plantilla' : 'Nueva Plantilla'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="template-form">
            <div>
              <Label>Nombre</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="Ej: Saludo inicial" data-testid="template-name-input" />
            </div>
            <div>
              <Label>Categoria (opcional)</Label>
              <Input value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} placeholder="Ej: Saludos, Precios" data-testid="template-category-input" />
            </div>
            <div>
              <Label>Contenido</Label>
              <Textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} required rows={5} placeholder="Escribe el mensaje..." data-testid="template-content-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); setEditingTemplate(null); resetForm(); }}>Cancelar</Button>
              <Button type="submit" data-testid="save-template-btn">{editingTemplate ? 'Guardar Cambios' : 'Crear Plantilla'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Templates;
